import {IQuestion} from '@services/live-event/live-event.model';
import {ILiveEvent} from '@services/live-events/live-events.model';
import {convertUtf8ToBase64} from '@utils/convertUtf8ToBase64';
import {getEnv} from './getEnv';

const NextUrls = {
    LOGOUT_SERVER: `/api/logout`,
    USER_GUESS_COUNTRY: `/api/user/guess-country`,
    ONBOARDING_SURVEY: `/api/survey`,
    PODCAST: `/api/podcast`,
    SALES_FUNNEL_TEXTS: `/api/sales-funnel-texts`,
    ONBOARDING_ZAPIER_HOOK: '/api/onboarding-zapier-hook',
    AUTHENTICATE: (refreshToken: string, path: string) =>
        `/api/a?t=${convertUtf8ToBase64(refreshToken)}&p=${convertUtf8ToBase64(path)}`,
    INVALIDATE_CACHE_TAG: `/api/invalidate-cache/tag`,
};

const ContentHubUrls = {
    CATEGORIES: '/v1/category/tree',
    COURSES: `/v1/courses`,
    COURSE: (slugOrId: string | number) => `/v1/course/${slugOrId}`,

    TRAINERS: `/v1/trainers`,
    TRAINER: (id: string) => `/v1/trainer/${id}`,
    HERO_SLIDER: '/v1/sliders',
    SUGGESTED_SEARCH: `/v1/suggest`,

    COURSE_FAVS: `/v1/user/course-favorites`,
    COURSE_FAV_UPDATE: (courseId: number) =>
        `/v1/user/course-favorite/${courseId}`,
    USER_COURSE_RECOMMENDATIONS: '/v1/user/course-recommendation/$locale',
    RECOMMENDATION_USER_PREFERENCE: '/v1/user/preference/$locale',
};

const GatewayUrls = {
    LOGIN: `/auth/token/create`,
    LOGIN_REFRESH: `/auth/token/refresh`,
    CHECK_USER_EXISTS: `/v2/$locale/register/check-user-exists`,

    PASSWORD_RESET: `/v2/$locale/reset-password/request-token`,
    NEW_PASSWORD: `/v2/$locale/reset-password/change`,

    CREATE_CSRF_TOKEN: `/v2/$locale/form/token`,
    REGISTER_CREATE_USER: `/v2/$locale/register/user`,

    CHECKOUT_PLANS: (currency: string) => `/v3/$locale/plan/${currency}`,
    CHECKOUT_PLAN: (planId: string) => `/v3/$locale/plan/${planId}`,
    CHECKOUT_CARD_CREATE_TOKEN: `/v2/$locale/checkout/credit-card/create-using-token`,
    CHECKOUT_DEBIT_CREATE_BANK_ACCOUNT: `/v2/$locale/checkout/debit/create-bank-account`,
    CHECKOUT_PAYPAL_CREATE_REDIRECT_URL: `/v2/$locale/checkout/paypal/create-redirect-url`,

    CHECKOUT_SUBSCRIPTION_CREATE: `/v2/$locale/checkout/subscription/create`,
    CHECKOUT_SUBSCRIPTION_UPGRADE: `/v2/$locale/checkout/subscription/upgrade`,
    CHECKOUT_VALIDATE_COUPON: (planId: string, couponCode: string) =>
        `/v2/$locale/checkout/coupon/validate/${planId}/${couponCode}`,

    COURSES_VIEWED: `/v2/$locale/course/viewed`,

    USER: `/v2/$locale/user`,
    USER_CHANGE_PRIMARY_PAYMENT_SOURCE: `/v2/$locale/user-profile/change-primary-payment-source`,
    USER_UPDATE_PASSWORD: `/v2/$locale/user-profile/change-password`,
    USER_CANCEL_SUBSCRIPTION: `/v2/$locale/user-profile/chargebee-hosted/pre-cancel`,
    USER_UPGRADE_SUBSCRIPTION: '/v3/$locale/checkout/subscription/upgrade',
    USER_RESUME_SUBSCRIPTION: (subscriptionId: string) =>
        `/v2/$locale/user-profile/subscription/uncancel/${subscriptionId}`,
    USER_GET_INVOICES: `/v2/$locale/user-profile/invoices`,
    USER_GET_INVOICE: (invoiceId: string) =>
        `/v2/$locale/user-profile/invoices/download-link/${invoiceId}`,
    USER_PAYMENT_DETAILS: `/v2/$locale/user-profile/payment/details`,
    USER_MANAGE_PAYMENT_METHODS: `/v2/$locale/user-profile/chargebee-hosted/manage-payment-methods`,
    USER_UPDATE_TIMEZONE: `/v3/$locale/user/timezone`,

    PURGE_CACHE_GW: '/$locale/cache/purge/full-page-cache',
};

const CommunityHubUrls = {
    SEARCH_WEBINAR: `/webinar/v2/search`,

    LIVE_EVENTS: `/webinar/v2/filter`,

    LIVE_EVENT: (slug: string) => `/webinar/v2/${slug}`,
    LIVE_EVENT_ENLIST: (webinarId: number) =>
        `/webinar/v1/user-enlist/${webinarId}`,
    LIVE_EVENT_DELIST: (webinarId: number) =>
        `/webinar/v1/user-delist/${webinarId}`,

    LIVE_EVENT_QUESTIONS: `/webinar-question/v2/filter`,
    LIVE_EVENT_QUESTION_CREATE: (liveEventId: ILiveEvent['id']) =>
        `/webinar-question/v1/create/${liveEventId}`,
    LIVE_EVENT_QUESTION_UPVOTE: (questionId: IQuestion['id']) =>
        `/webinar-question/v1/upvote/${questionId}`,
    LIVE_EVENT_QUESTION_REMOVE_UPVOTE: (questionId: IQuestion['id']) =>
        `/webinar-question/v1/remove-upvote/${questionId}`,

    COMMUNITY_AVATAR: `/community-user/v1/avatar`,
    COMMUNITY_PROFILE: `/community-user/v1/profile`,
    UPDATE_USERNAME: `/community-user/v1/update-username`,

    WEBINAR_CHAT_RETRIEVE_TOKEN: (webinarId: number) =>
        `/webinar-chat/v2/retrieveToken/${webinarId}`,
    WEBINAR_CHAT_SEND_MESSAGE: (webinarId: number) =>
        `/webinar-chat/v2/send-message/${webinarId}`,
    WEBINAR_GET_MESSAGES: (webinarId: number) =>
        `/webinar-chat/v2/get-messages/${webinarId}`,

    CLAIM_COUPON: (couponSlug: string) => `/coupon/v2/claim/${couponSlug}`,

    DISCUSSION_SPACE_CONTRIBUTIONS: (slug: string) =>
        `/discussion-space/v2/${slug}/contributions`,

    TRANSCRIBE_SPEECH: `/voice/v1/transcribe`,

    LOGBOOK_ACTIVITY: `/logbook/v1/activity`,
    LOGBOOK_HORSES: `/logbook/v1/horses`,
};

const UBAUrls = {
    ACTIVITY: `/$locale/activity-log/activity`,
    WATCH_TIME: (courseId: number) => `/$locale/course/watch-time/${courseId}`,
    WATCH_TIME_COLLECTION: `/$locale/course/watch-time-collection`,
    UPDATE_WATCH_TIME: (courseId: number, videoId: number) =>
        `/$locale/course/watch-time/${courseId}/${videoId}`,
    ONBOARDING_SURVEY_SUBMIT: (answerId: string) =>
        `/$locale/post-purchase/${answerId}`,
    ONBOARDING_USER_TYPE_SUBMIT: `/$locale/onboarding/user-type`,
    SUPER_COURSE_COMPLETED_LESSONS: (superCourseId: number) =>
        `/$locale/super-course/completed-lessons/${superCourseId}`,
    COURSE_RESET_PROGRESS: (courseId: number) =>
        `/$locale/courses/progress/${courseId}`,
    SUPER_COURSE_MARK_LESSON_COMPLETE: `/$locale/super-course/mark-lesson-completed`,
    SUPER_COURSE_UNMARK_LESSON_COMPLETE: (lessonId: number) =>
        `/$locale/super-course/mark-lesson-not-completed/${lessonId}`,
    LIVE_EVENT_RECORDING_WATCHTIME: (id: string) =>
        `/$locale/webinar/recording-watchtime/${id}`,
    UPDATE_LIVE_EVENT_RECORDING_WATCHTIME: (
        webinarId: number,
        recordingId: number,
        watchTime: number,
    ) =>
        `/$locale/webinar/recording-watchtime/${webinarId}/${recordingId}/${watchTime}`,
    COURSE_PROGRESS_BULK: `/$locale/courses/progress`,
    CONTINUE_WATCHING: `/$locale/continue-watching`,
    COMPLETED_COURSES: `/$locale/completed-courses`,
};

const TrackingUrls = {
    TRACK_VIDEO_WATCHED: getEnv('NEXT_PUBLIC_URL_VIDEO_TRACKING'),
};

const AdminUrls = {
    ADMIN_COURSE_EDIT: (id: number) => `/course/edit/${id}`,
};

const NotificationHubUrls = {
    FEED: `/feed`,
    FEED_MARK_AS_READ: (feedId: number) => `/feed/${feedId}/read`,
};

const prependBase = <T extends object>(urls: T, base: string): T => {
    const prependToValue = (
        value: string | ((...args: unknown[]) => string),
    ) => {
        if (typeof value === 'function') {
            return (...args: unknown[]) => base + value(...args);
        }
        return base + value;
    };
    return Object.fromEntries(
        Object.entries(urls).map(([key, value]) => [
            key,
            prependToValue(value),
        ]),
    ) as T;
};

export const getUrls = () => ({
    ...prependBase(GatewayUrls, getEnv('NEXT_PUBLIC_URL_API_GATEWAY')),
    ...prependBase(CommunityHubUrls, getEnv('NEXT_PUBLIC_URL_COMMUNITY_HUB')),
    ...prependBase(UBAUrls, getEnv('NEXT_PUBLIC_URL_USER_BEHAVIOR_API_BASE')),
    ...prependBase(AdminUrls, getEnv('NEXT_PUBLIC_URL_ADMIN_BASE')),
    ...prependBase(ContentHubUrls, getEnv('NEXT_PUBLIC_URL_CONTENT_HUB')),
    ...prependBase(
        NotificationHubUrls,
        getEnv('NEXT_PUBLIC_URL_NOTIFICATION_HUB'),
    ),
    ...NextUrls,
    ...TrackingUrls,
});
